import { auth } from "../firebase";
import { dateTimeFormatter } from "./dateFormatters";

// Every automated contact-log entry is prefixed with this so it's visually distinct from
// entries staff type themselves. Exported since PdfTimesheet.jsx's direct-send path (not
// detected via Gmail polling, so it doesn't go through checkPendingDrafts below) uses the
// same convention for its own automated log entries.
export const AUTOMATION_PREFIX = "[◉_◉]";

function threadUrl(threadId) {
  return `https://mail.google.com/mail/u/0/#all/${threadId}`;
}

function draftTypeLabel({ pdfType, markPaid }) {
  if (pdfType === "Quote") return "Quote draft";
  return markPaid ? "Paid-invoice draft" : "Invoice draft";
}

function markedLabel({ markPaid, markCert }) {
  if (markPaid && markCert) return "Sent, Paid and Cert";
  if (markPaid) return "Sent and Paid";
  return "Sent";
}

// Quotes don't have a Sent/Paid/Cert box - "Sent" is really an invoice-paid-status field (see
// checkPendingDrafts below) - so a sent quote draft gets its own note instead of claiming
// something was "marked".
function buildSentLogEntry(pendingDraft) {
  let label = draftTypeLabel(pendingDraft);
  let now = new Date();

  let note =
    pendingDraft.pdfType === "Quote"
      ? `${AUTOMATION_PREFIX} ${label} sent via [Gmail](${threadUrl(pendingDraft.threadId)}) — status set to Quote Sent.`
      : `${AUTOMATION_PREFIX} ${label} sent via [Gmail](${threadUrl(pendingDraft.threadId)}) — marked ${markedLabel(pendingDraft)} and status set to Completed automatically.`;

  return {
    icon: "mail",
    note,
    date: dateTimeFormatter(now, "d_full_variant"),
    time: dateTimeFormatter(now, "t_short"),
  };
}

function buildDiscardedLogEntry() {
  let now = new Date();

  return {
    icon: "mail",
    note: `${AUTOMATION_PREFIX} Draft discarded in Gmail without being sent.`,
    date: dateTimeFormatter(now, "d_full_variant"),
    time: dateTimeFormatter(now, "t_short"),
  };
}

// Checks every still-pending draft against Gmail (via the backend, which holds the OAuth
// credentials) and works out what should change as a result - which pendingDrafts entries
// have resolved (and so should be dropped from tracking), any Sent/Paid/Cert fields that
// need setting, and the contact-log entries to append. Doesn't perform any writes itself -
// the caller applies the result in one updateDoc, alongside whatever else it already knows
// about the record (e.g. the current callLog array to append to).
export async function checkPendingDrafts(pendingDrafts) {
  let stillPending = [];
  let fieldUpdates = {};
  let newLogEntries = [];

  for (let entry of pendingDrafts) {
    let result;
    try {
      let idToken = await auth.currentUser.getIdToken();
      let response = await fetch("/api/checkDraftStatus", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          draftId: entry.draftId,
          threadId: entry.threadId,
          createdAt: entry.createdAt,
        }),
      });
      result = response.ok ? await response.json() : null;
    } catch (err) {
      console.error("Error checking pending draft status", err);
      result = null;
    }

    if (!result || result.status === "pending" || result.status === "scheduled") {
      // Still pending, scheduled via Gmail's Schedule send but not gone out yet, or the check
      // itself failed (network hiccup, transient API error) - leave it in the array to retry
      // next time rather than guessing at an outcome.
      stillPending.push(entry);
      continue;
    }

    if (result.status === "sent") {
      // Backfills threadId on any primary that didn't already have one (e.g. created the
      // traditional way, not via the Gmail Add-on) - once its first email is confirmed sent,
      // it adopts that thread, so the picker can match deterministically on future emails too.
      fieldUpdates.threadId = entry.threadId;

      if (entry.pdfType === "Quote") {
        // The Sent/Paid/Cert boxes are an invoice-paid-status thing, not relevant to a quote -
        // a sent quote instead moves the primary's own status forward.
        fieldUpdates.status = "quote-sent";
      } else {
        // An invoice going out means the job itself is finished.
        fieldUpdates.status = "completed";
        fieldUpdates.sent = true;
        if (entry.markPaid) fieldUpdates.paid = true;
        if (entry.markCert) fieldUpdates.cert = true;
      }

      newLogEntries.push(buildSentLogEntry(entry));
    } else if (result.status === "discarded") {
      newLogEntries.push(buildDiscardedLogEntry());
    }
    // Resolved either way - not added back to stillPending.
  }

  return {
    stillPending,
    hasPendingDrafts: stillPending.length > 0,
    fieldUpdates,
    newLogEntries,
  };
}

// Turns a checkPendingDrafts() result into the Firestore update its caller should apply, or
// null if nothing actually changed - shared by PrimaryForm's single-record check and
// useBatchPendingDraftsCheck's list-page check, so a future change to this shape (e.g. a new
// field to propagate) only needs updating here instead of at both call sites. Not shared
// with functions/index.js's mirror of this same logic - that's a separate deployment
// (Cloud Functions) with no access to this frontend module.
export function buildPendingDraftsUpdate(record, result) {
  let pendingDrafts = record.pendingDrafts || [];
  let hasChanges =
    Object.keys(result.fieldUpdates).length > 0 ||
    result.newLogEntries.length > 0 ||
    result.stillPending.length !== pendingDrafts.length;

  if (!hasChanges) return null;

  return {
    ...result.fieldUpdates,
    pendingDrafts: result.stillPending,
    hasPendingDrafts: result.hasPendingDrafts,
    callLog: [...(record.callLog || []), ...result.newLogEntries],
  };
}
